import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Smartphone, 
  Download, 
  Settings, 
  Play, 
  CheckCircle, 
  AlertTriangle,
  FileCode,
  Terminal,
  Folder,
  Package,
  Key,
  Upload,
  Info,
  ExternalLink,
  Copy,
  Check,
  Cloud,
  CloudUpload,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const AndroidPage = () => {
  const { api } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(null);
  const [onedriveStatus, setOnedriveStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Check OneDrive status on mount
  useEffect(() => {
    checkOnedriveStatus();
  }, []);

  const checkOnedriveStatus = async () => {
    setLoadingStatus(true);
    try {
      const response = await api().get('/android/onedrive-status');
      setOnedriveStatus(response.data);
    } catch (error) {
      console.error('Error checking OneDrive status:', error);
      setOnedriveStatus({ exists: false, error: true });
    } finally {
      setLoadingStatus(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(id);
    toast.success('Comando copiado al portapapeles');
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const handleUploadToOnedrive = async () => {
    setUploading(true);
    try {
      const response = await api().post('/android/upload-to-onedrive');
      toast.success('Proyecto subido a OneDrive exitosamente');
      setOnedriveStatus({ exists: true, file: response.data.file });
      checkOnedriveStatus(); // Refresh status
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al subir a OneDrive';
      toast.error(message);
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFromOnedrive = async () => {
    setDownloading(true);
    try {
      const response = await api().get('/android/download-from-onedrive');
      
      if (response.data.downloadUrl) {
        // Open OneDrive download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
        toast.success('Descarga iniciada desde OneDrive');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al descargar desde OneDrive';
      toast.error(message);
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadProject = async () => {
    setDownloading(true);
    try {
      const response = await api().get('/android/download-project', {
        responseType: 'blob'
      });
      
      // Check if response is JSON (redirect to OneDrive)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        if (json.redirect && json.downloadUrl) {
          window.open(json.downloadUrl, '_blank');
          toast.success('Descarga iniciada desde OneDrive');
          return;
        }
      }
      
      // Regular blob download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cm-idiomas-android.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Proyecto Android descargado exitosamente');
    } catch (error) {
      // Check if error response has JSON with download URL
      if (error.response?.data) {
        try {
          const text = await error.response.data.text?.();
          if (text) {
            const json = JSON.parse(text);
            if (json.downloadUrl) {
              window.open(json.downloadUrl, '_blank');
              toast.success('Descarga iniciada desde OneDrive');
              return;
            }
          }
        } catch (e) {}
      }
      toast.error('Error al descargar el proyecto. Intente descargar desde OneDrive.');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('es-MX');
  };

  const CommandBlock = ({ command, id }) => (
    <div className="relative group">
      <pre className="bg-zinc-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto font-mono">
        {command}
      </pre>
      <Button
        size="sm"
        variant="ghost"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => copyToClipboard(command, id)}
      >
        {copiedCommand === id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Smartphone className="h-6 w-6 text-primary" />
            Aplicación Android
          </h1>
          <p className="text-muted-foreground mt-1">
            Genera y compila la aplicación móvil para Android
          </p>
        </div>
        <Badge variant="outline" className="text-primary border-primary">
          Capacitor v5
        </Badge>
      </div>

      {/* Descarga del proyecto */}
      <Card className="border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            Descargar Proyecto Android
          </CardTitle>
          <CardDescription>
            Descarga el proyecto completo listo para abrir en Android Studio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Local Download */}
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleDownloadProject} 
              disabled={downloading}
              className="bg-primary hover:bg-primary/90"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparando...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar cm-idiomas-android.zip
                </>
              )}
            </Button>
            <span className="text-sm text-muted-foreground">
              ~50 MB • Incluye todo lo necesario
            </span>
          </div>

          {/* OneDrive Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="h-4 w-4 text-blue-500" />
              <span className="font-medium text-sm">Almacenamiento en OneDrive</span>
              <Badge variant="outline" className="text-xs">Para producción</Badge>
            </div>
            
            <p className="text-sm text-muted-foreground mb-3">
              Si la descarga directa no funciona en tu hosting (Railway, etc.), sube el proyecto a OneDrive 
              y descárgalo desde ahí.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button 
                onClick={handleUploadToOnedrive} 
                disabled={uploading}
                variant="outline"
                className="border-blue-500/50 hover:bg-blue-500/10"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Subiendo...
                  </>
                ) : (
                  <>
                    <CloudUpload className="h-4 w-4 mr-2" />
                    Subir a OneDrive
                  </>
                )}
              </Button>

              <Button 
                onClick={handleDownloadFromOnedrive} 
                disabled={downloading || !onedriveStatus?.exists}
                variant="outline"
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar desde OneDrive
              </Button>

              <Button
                onClick={checkOnedriveStatus}
                variant="ghost"
                size="icon"
                disabled={loadingStatus}
              >
                <RefreshCw className={`h-4 w-4 ${loadingStatus ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* OneDrive Status */}
            {onedriveStatus && (
              <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                {onedriveStatus.exists ? (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>
                      Archivo disponible en OneDrive 
                      {onedriveStatus.file?.size && ` • ${formatFileSize(onedriveStatus.file.size)}`}
                      {onedriveStatus.file?.lastModified && ` • Actualizado: ${formatDate(onedriveStatus.file.lastModified)}`}
                    </span>
                  </div>
                ) : onedriveStatus.error ? (
                  <div className="flex items-center gap-2 text-sm text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>No se pudo verificar OneDrive. Asegúrate de que esté conectado en Backups.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Info className="h-4 w-4" />
                    <span>El archivo se subirá automáticamente a OneDrive cuando hagas clic en "Descargar desde OneDrive".</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Instrucciones en tabs */}
      <Tabs defaultValue="requisitos" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="requisitos">1. Requisitos</TabsTrigger>
          <TabsTrigger value="setup">2. Configuración</TabsTrigger>
          <TabsTrigger value="build">3. Compilar APK</TabsTrigger>
          <TabsTrigger value="firmar">4. Firmar APK</TabsTrigger>
          <TabsTrigger value="mdm">5. Desplegar MDM</TabsTrigger>
        </TabsList>

        {/* TAB 1: Requisitos */}
        <TabsContent value="requisitos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Requisitos del Sistema
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Software Necesario
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Badge variant="outline" className="mt-0.5">1</Badge>
                      <div>
                        <strong>Android Studio</strong> (Arctic Fox o superior)
                        <br />
                        <a 
                          href="https://developer.android.com/studio" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          Descargar Android Studio <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <Badge variant="outline" className="mt-0.5">2</Badge>
                      <div>
                        <strong>Java Development Kit (JDK) 17</strong>
                        <br />
                        <span className="text-muted-foreground">Incluido en Android Studio</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <Badge variant="outline" className="mt-0.5">3</Badge>
                      <div>
                        <strong>Android SDK</strong> (API Level 33 o superior)
                        <br />
                        <span className="text-muted-foreground">Se instala desde Android Studio</span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    Requisitos de Hardware
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• RAM: Mínimo 8 GB (16 GB recomendado)</li>
                    <li>• Disco: 10 GB libres mínimo</li>
                    <li>• Windows 10/11, macOS 10.14+, o Linux</li>
                  </ul>
                </div>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Si es la primera vez que usas Android Studio, la instalación inicial puede tomar 30-60 minutos 
                  mientras descarga los SDKs necesarios.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: Configuración */}
        <TabsContent value="setup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Folder className="h-5 w-5" />
                Configuración del Proyecto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-semibold">Paso 1: Extraer el proyecto</h4>
                <p className="text-sm text-muted-foreground">
                  Descomprime el archivo <code className="bg-muted px-1 rounded">cm-idiomas-android.zip</code> 
                  en una carpeta de tu elección.
                </p>
                <CommandBlock 
                  command="# Estructura del proyecto extraído:
cm-idiomas-android/
├── app/                    # Código de la aplicación
│   ├── src/
│   │   └── main/
│   │       ├── AndroidManifest.xml
│   │       ├── assets/     # Web assets (tu app React)
│   │       ├── java/       # Código nativo
│   │       └── res/        # Recursos (iconos, strings)
│   └── build.gradle
├── gradle/
├── build.gradle
├── settings.gradle
└── capacitor.config.json"
                  id="estructura"
                />
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Paso 2: Abrir en Android Studio</h4>
                <ol className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Badge>1</Badge>
                    <span>Abre Android Studio</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>2</Badge>
                    <span>Selecciona <strong>"Open"</strong> (no "New Project")</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>3</Badge>
                    <span>Navega a la carpeta <code className="bg-muted px-1 rounded">cm-idiomas-android</code></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>4</Badge>
                    <span>Espera a que Gradle sincronice (puede tomar varios minutos la primera vez)</span>
                  </li>
                </ol>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Paso 3: Configurar la URL del servidor</h4>
                <p className="text-sm text-muted-foreground">
                  Edita el archivo <code className="bg-muted px-1 rounded">app/src/main/assets/capacitor.config.json</code>
                  para apuntar a tu servidor de producción:
                </p>
                <CommandBlock 
                  command={`{
  "appId": "com.cmidiomas.inventario",
  "appName": "CM Idiomas Inventario",
  "webDir": "public",
  "server": {
    "url": "https://TU-DOMINIO.com",  // <-- Cambia esto
    "cleartext": false
  }
}`}
                  id="config"
                />
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Importante:</strong> Si tu servidor usa HTTP (no HTTPS), debes agregar 
                    <code className="bg-muted px-1 rounded mx-1">"cleartext": true</code> en la configuración.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 3: Compilar */}
        <TabsContent value="build" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5" />
                Compilar el APK
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-semibold">Opción A: Desde Android Studio (Recomendado)</h4>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <Badge>1</Badge>
                    <span>En el menú superior, ve a <strong>Build → Build Bundle(s) / APK(s) → Build APK(s)</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>2</Badge>
                    <span>Espera a que termine la compilación (2-5 minutos)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>3</Badge>
                    <span>
                      El APK estará en:
                      <code className="bg-muted px-1 rounded block mt-1">
                        app/build/outputs/apk/debug/app-debug.apk
                      </code>
                    </span>
                  </li>
                </ol>
              </div>

              <div className="border-t pt-6 space-y-4">
                <h4 className="font-semibold">Opción B: Desde línea de comandos</h4>
                <p className="text-sm text-muted-foreground">
                  Abre una terminal en la carpeta del proyecto:
                </p>
                <CommandBlock 
                  command={`# Windows
.\\gradlew assembleDebug

# macOS / Linux
./gradlew assembleDebug`}
                  id="gradle-build"
                />
              </div>

              <Alert className="border-green-500/50 bg-green-500/10">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <AlertDescription>
                  <strong>APK de Debug:</strong> Este APK es funcional para pruebas y distribución interna vía MDM.
                  No requiere firma para instalación manual.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 4: Firmar */}
        <TabsContent value="firmar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Firmar APK para Producción
              </CardTitle>
              <CardDescription>
                Opcional: Solo necesario si quieres publicar en Play Store o requieres firma corporativa
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-semibold">Paso 1: Generar Keystore</h4>
                <p className="text-sm text-muted-foreground">
                  Si no tienes un keystore, genera uno nuevo:
                </p>
                <CommandBlock 
                  command={`keytool -genkey -v -keystore cm-idiomas.keystore -alias cm-idiomas -keyalg RSA -keysize 2048 -validity 10000

# Te pedirá:
# - Contraseña del keystore
# - Nombre, Organización, Ciudad, País
# - Contraseña del alias (puede ser la misma)`}
                  id="keytool"
                />
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>¡GUARDA EL KEYSTORE!</strong> Si lo pierdes, no podrás actualizar la app.
                    Guárdalo en un lugar seguro junto con las contraseñas.
                  </AlertDescription>
                </Alert>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold">Paso 2: Compilar APK firmado</h4>
                <ol className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Badge>1</Badge>
                    <span>En Android Studio: <strong>Build → Generate Signed Bundle / APK</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>2</Badge>
                    <span>Selecciona <strong>APK</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>3</Badge>
                    <span>Selecciona tu keystore y llena las contraseñas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>4</Badge>
                    <span>Selecciona <strong>release</strong> como Build Variant</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>5</Badge>
                    <span>
                      El APK firmado estará en:
                      <code className="bg-muted px-1 rounded block mt-1">
                        app/release/app-release.apk
                      </code>
                    </span>
                  </li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 5: MDM */}
        <TabsContent value="mdm" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Desplegar vía MDM
              </CardTitle>
              <CardDescription>
                Distribuye la app a los dispositivos corporativos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-semibold">Pasos generales para MDM</h4>
                <ol className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <Badge>1</Badge>
                    <div>
                      <strong>Sube el APK a tu consola MDM</strong>
                      <p className="text-muted-foreground">
                        La ubicación varía según el proveedor (Intune, VMware Workspace ONE, SOTI, etc.)
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>2</Badge>
                    <div>
                      <strong>Configura el grupo de dispositivos</strong>
                      <p className="text-muted-foreground">
                        Selecciona los dispositivos o grupos que recibirán la app
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>3</Badge>
                    <div>
                      <strong>Establece la política de instalación</strong>
                      <p className="text-muted-foreground">
                        Recomendado: Instalación automática y silenciosa
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Badge>4</Badge>
                    <div>
                      <strong>Despliega</strong>
                      <p className="text-muted-foreground">
                        La app se instalará en los dispositivos según la política configurada
                      </p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card className="bg-muted/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Microsoft Intune</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground">
                    Apps → Android → Add → Line-of-business app → Seleccionar APK
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">VMware Workspace ONE</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs text-muted-foreground">
                    Apps & Books → Applications → Native → Add Application
                  </CardContent>
                </Card>
              </div>

              <Alert className="border-primary/50 bg-primary/10">
                <Info className="h-4 w-4 text-primary" />
                <AlertDescription>
                  <strong>Identificador de la app:</strong>{' '}
                  <code className="bg-muted px-1 rounded">com.cmidiomas.inventario</code>
                  <br />
                  Usa este ID para configurar políticas específicas en tu MDM.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Información técnica adicional */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCode className="h-5 w-5" />
            Información Técnica
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Identificadores</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li><strong>App ID:</strong> com.cmidiomas.inventario</li>
                <li><strong>Versión:</strong> 1.0.0</li>
                <li><strong>Min SDK:</strong> Android 6.0 (API 23)</li>
                <li><strong>Target SDK:</strong> Android 14 (API 34)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Permisos requeridos</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• INTERNET</li>
                <li>• CAMERA (para QR)</li>
                <li>• READ/WRITE_STORAGE</li>
                <li>• ACCESS_NETWORK_STATE</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Tecnologías</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Capacitor 5.x</li>
                <li>• React 18</li>
                <li>• WebView optimizado</li>
                <li>• Plugins nativos</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AndroidPage;
